import { useState, useEffect, useCallback, useRef } from 'react';
import { User, SessionInfo } from '../types';
import { useAuditLog } from './useAuditLog';

interface SessionConfig {
  timeoutMinutes: number;
  warningMinutes: number;
  maxInactivityMinutes: number;
}

const DEFAULT_CONFIG: SessionConfig = {
  timeoutMinutes: 30, // 30 minutes of inactivity
  warningMinutes: 5,  // Show warning 5 minutes before timeout
  maxInactivityMinutes: 60 // Force logout after 60 minutes
};

export function useSession(
  user: User | null,
  onTimeout: () => void,
  config: Partial<SessionConfig> = {}
) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const { logAction } = useAuditLog();
  
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [timeUntilTimeout, setTimeUntilTimeout] = useState<number>(0);
  const [showWarning, setShowWarning] = useState(false);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<Date>(new Date());

  // Initialize session when user logs in
  useEffect(() => {
    if (user && !sessionInfo) {
      const newSession: SessionInfo = {
        id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId: user.uid,
        startTime: new Date(),
        lastActivity: new Date(),
        isActive: true,
        timeout: mergedConfig.timeoutMinutes
      };
      
      setSessionInfo(newSession);
      lastActivityRef.current = new Date();
      
      // Log session start
      logAction(user, 'login', {
        sessionId: newSession.id,
        startTime: newSession.startTime
      });
    } else if (!user && sessionInfo) {
      // Log session end
      if (sessionInfo.isActive) {
        setSessionInfo(prev => prev ? { ...prev, isActive: false } : null);
      }
      
      setSessionInfo(null);
      setTimeUntilTimeout(0);
      setShowWarning(false);
    }
  }, [user, sessionInfo, logAction, mergedConfig]);

  // Update last activity
  const updateActivity = useCallback(() => {
    if (!sessionInfo || !sessionInfo.isActive) return;
    
    const now = new Date();
    lastActivityRef.current = now;
    
    setSessionInfo(prev => prev ? {
      ...prev,
      lastActivity: now
    } : null);
    
    // Reset timeout timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
    }
    
    // Set warning timer
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
    }, (mergedConfig.timeoutMinutes - mergedConfig.warningMinutes) * 60 * 1000);
    
    // Set timeout timer
    timeoutRef.current = setTimeout(() => {
      handleTimeout();
    }, mergedConfig.timeoutMinutes * 60 * 1000);
    
    setTimeUntilTimeout(mergedConfig.timeoutMinutes * 60);
  }, [sessionInfo, mergedConfig]);

  // Handle session timeout
  const handleTimeout = useCallback(async () => {
    if (!sessionInfo || !user) return;
    
    setSessionInfo(prev => prev ? { ...prev, isActive: false } : null);
    
    // Log session timeout
    await logAction(user, 'session_timeout', {
      sessionId: sessionInfo.id,
      duration: Date.now() - sessionInfo.startTime.getTime(),
      lastActivity: lastActivityRef.current
    });
    
    onTimeout();
  }, [sessionInfo, user, logAction, onTimeout]);

  // Extend session
  const extendSession = useCallback(() => {
    if (!sessionInfo || !sessionInfo.isActive) return;
    
    setShowWarning(false);
    updateActivity();
  }, [sessionInfo, updateActivity]);

  // Force logout
  const forceLogout = useCallback(async () => {
    if (!sessionInfo || !user) return;
    
    setSessionInfo(prev => prev ? { ...prev, isActive: false } : null);
    
    await logAction(user, 'logout', {
      sessionId: sessionInfo.id,
      duration: Date.now() - sessionInfo.startTime.getTime(),
      forced: true
    });
    
    onTimeout();
  }, [sessionInfo, user, logAction, onTimeout]);

  // Set up activity listeners
  useEffect(() => {
    if (!sessionInfo || !sessionInfo.isActive) return;
    
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];
    
    const handleActivity = () => {
      updateActivity();
    };
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [sessionInfo, updateActivity]);

  // Update countdown timer
  useEffect(() => {
    if (!sessionInfo || !sessionInfo.isActive || !showWarning) {
      setTimeUntilTimeout(0);
      return;
    }
    
    const interval = setInterval(() => {
      const now = new Date();
      const timeSinceActivity = (now.getTime() - lastActivityRef.current.getTime()) / 1000 / 60;
      const remaining = Math.max(0, mergedConfig.timeoutMinutes - timeSinceActivity);
      
      setTimeUntilTimeout(Math.ceil(remaining * 60));
      
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [sessionInfo, showWarning, mergedConfig]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningRef.current) {
        clearTimeout(warningRef.current);
      }
    };
  }, []);

  return {
    sessionInfo,
    timeUntilTimeout,
    showWarning,
    extendSession,
    forceLogout,
    updateActivity
  };
}









